import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { ResolvedUserAvatar } from "../UserAvatar";
import { useUserAvatar } from "../../../hooks/useUserAvatar";
import { pickFriendshipActionIcon } from "../../atoms/icons/FriendshipActionIcons";
import "./index.scss";

const INTERACTIVE_CHILD_SELECTOR = '[data-em-interactive="true"]';

const resolveVariantClass = (variant) => {
  switch (variant) {
    case "primary":
    case "default":
      return "em-action--connect";
    case "success":
      return "em-action--accept";
    case "danger":
      return "em-action--danger";
    case "secondary":
    default:
      return "em-action--secondary";
  }
};

const EditorialMemberCard = ({
  userId,
  username,
  displayName,
  roleText,
  roleContent,
  locationText,
  actions = [],
  onCardClick,
  cardAriaLabel,
  avatarAlt,
  avatarUrl,
  presenceStatus = null,
  publicMedia = false,
  muted = false,
  badgeText = null,
}) => {
  const { t } = useTranslation();
  const {
    avatarUrl: resolvedAvatarUrl,
    shouldUseSupportAvatar,
  } = useUserAvatar(userId, {
    username,
    enabled: !avatarUrl,
    audience: publicMedia ? 'public' : 'viewer',
  });
  const displayAvatarUrl = avatarUrl || resolvedAvatarUrl;
  const shouldRenderSupportAvatar = !avatarUrl && shouldUseSupportAvatar;
  const resolvedDisplayName = displayName || username;

  const handle = useMemo(
    () => (username ? username.replace(/\s+/g, "").toLowerCase() : ""),
    [username]
  );

  const resolvedAvatarAlt = avatarAlt || resolvedDisplayName || t("friendsPage.userList.avatarFallback");
  const isCardClickable = typeof onCardClick === "function";
  const normalizedActions = actions.filter(Boolean);
  const resolvedRoleContent = roleContent ?? roleText;

  const handleCardActivate = useCallback(
    (event) => {
      if (!isCardClickable) return;
      const target = event?.target;
      if (target instanceof Element && target.closest(INTERACTIVE_CHILD_SELECTOR)) {
        return;
      }
      onCardClick();
    },
    [isCardClickable, onCardClick]
  );

  const handleCardKeyDown = useCallback(
    (event) => {
      if (!isCardClickable) return;
      if (event.defaultPrevented || event.target !== event.currentTarget) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onCardClick();
      }
    },
    [isCardClickable, onCardClick]
  );

  const stopInteractive = useCallback((event) => {
    event.stopPropagation();
  }, []);

  return (
    <article
      className={`em-card${isCardClickable ? " em-card--clickable" : ""}${muted ? " em-card--muted" : ""}`}
      onClick={isCardClickable ? handleCardActivate : undefined}
      role={isCardClickable ? "button" : undefined}
      tabIndex={isCardClickable ? 0 : undefined}
      onKeyDown={isCardClickable ? handleCardKeyDown : undefined}
      aria-label={isCardClickable ? (cardAriaLabel || `Preview ${resolvedDisplayName || "profile"}`) : undefined}
    >
      <div className="em-card__img">
        {shouldRenderSupportAvatar ? (
          <ResolvedUserAvatar
            alt={resolvedAvatarAlt}
            avatarUrl={displayAvatarUrl}
            shouldUseSupportAvatar={shouldRenderSupportAvatar}
            className="em-card__avatar"
            size="100%"
            radius={0}
            imgProps={{ loading: "lazy" }}
          />
        ) : displayAvatarUrl ? (
          <img
            src={displayAvatarUrl}
            alt={resolvedAvatarAlt}
            loading="lazy"
            onError={(event) => {
              event.target.style.display = "none";
            }}
          />
        ) : null}
        {presenceStatus?.online ? (
          <span className="em-card__presence" aria-hidden="true" />
        ) : null}
      </div>

      {normalizedActions.length > 0 ? (
        <div
          className="em-card__actions"
          data-em-interactive="true"
          onClick={stopInteractive}
          onPointerDown={stopInteractive}
        >
          {normalizedActions.map((action) => {
            const Icon = pickFriendshipActionIcon(action);
            const variantClass = resolveVariantClass(action.variant);
            const ariaLabel = action.ariaLabel || action.label;
            return (
              <button
                key={action.key || action.label}
                type="button"
                className={`em-action ${variantClass}`}
                aria-label={ariaLabel}
                title={action.label}
                disabled={action.disabled || action.busy}
                aria-busy={action.busy || undefined}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (action.disabled || action.busy) return;
                  action.onClick?.();
                }}
              >
                <Icon className="em-action__icon" />
              </button>
            );
          })}
        </div>
      ) : null}

      {badgeText ? (
        <span className="em-card__badge">{badgeText}</span>
      ) : null}

      <div className="em-card__body">
        {resolvedRoleContent ? <div className="em-card__role">{resolvedRoleContent}</div> : null}
        {resolvedDisplayName ? <div className="em-card__name">{resolvedDisplayName}</div> : null}
        {handle ? <div className="em-card__handle">@{handle}</div> : null}
      </div>

      {locationText ? <div className="em-card__location">{locationText}</div> : null}
    </article>
  );
};

EditorialMemberCard.propTypes = {
  userId: PropTypes.string.isRequired,
  username: PropTypes.string,
  displayName: PropTypes.string,
  roleText: PropTypes.string,
  roleContent: PropTypes.node,
  locationText: PropTypes.string,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      ariaLabel: PropTypes.string,
      variant: PropTypes.oneOf(["primary", "default", "secondary", "success", "danger"]),
      disabled: PropTypes.bool,
      busy: PropTypes.bool,
      onClick: PropTypes.func,
    })
  ),
  onCardClick: PropTypes.func,
  cardAriaLabel: PropTypes.string,
  avatarAlt: PropTypes.string,
  avatarUrl: PropTypes.string,
  publicMedia: PropTypes.bool,
  presenceStatus: PropTypes.shape({
    online: PropTypes.bool,
    lastSeen: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  }),
  // Dim the whole card to signal a not-yet-confirmed member (e.g. a pending invite).
  muted: PropTypes.bool,
  // Small pill overlay (e.g. "Invited") describing the card's status.
  badgeText: PropTypes.node,
};

export default EditorialMemberCard;
